import moment from 'moment'
import { BadgeColor } from 'app/components/ui/badge'
import { formatCurrency } from 'lib/formatCurrency'
import { isTreatmentCode, type CategoryResolver } from 'lib/cdtCodeDefaults'
import { TreatmentCard, TreatmentStatus } from './treatmentFixture'

/**
 * The year appears only when it is not this one: practices book a year ahead
 * and the backlog band reaches years back, so a bare "Feb 23" could be either.
 */
export function shortDate(value: string | null | undefined, long = false): string | undefined {
  // Undefined rather than moment's "Invalid date": plenty of real records
  // carry no date at all.
  if (!value) return undefined
  const date = moment.utc(value)
  if (!date.isValid()) return undefined
  const month = long ? `MMMM D` : `MMM D`
  return date.isSame(moment.utc(), `year`) ? date.format(month) : date.format(`${month}, YYYY`)
}

/**
 * Acceptance is inferred from what happened to the work: the PMS's own
 * `accepted` flag is false on every plan in the building. Scheduled and
 * completed are a yes, broken is a yes whose appointment fell over, open is
 * the only one nobody has answered.
 */
export const STATUS_META: Record<TreatmentStatus, { label: string; bar: string; badge: BadgeColor }> = {
  completed: { label: `Completed`, bar: `bg-green-500`, badge: `green` },
  scheduled: { label: `Scheduled`, bar: `bg-blue-500`, badge: `blue` },
  broken: { label: `Broken`, bar: `bg-red-500`, badge: `red` },
  open: { label: `Unscheduled`, bar: `bg-amber-400`, badge: `amber` },
}

/**
 * The same four words as the chips and the legend. The headings used to
 * paraphrase ("Never booked", "On the schedule") while the chip on the row
 * beneath said "Open" or "Scheduled", so one status had two names on one
 * screen.
 */
export const APPOINTMENT_HEADINGS: Record<TreatmentStatus, string> = {
  broken: STATUS_META.broken.label,
  scheduled: STATUS_META.scheduled.label,
  open: STATUS_META.open.label,
  completed: STATUS_META.completed.label,
}

/** Order matters: the story runs completed, scheduled, then still to win. */
export const STATUS_ORDER: TreatmentStatus[] = [`completed`, `scheduled`, `broken`, `open`]

/**
 * The dialog reads in conversation order: what fell through first (the reason
 * to call), then what still stands, then history.
 */
export const DIALOG_ORDER: TreatmentStatus[] = [`broken`, `scheduled`, `open`, `completed`]

export function statusTotals(card: TreatmentCard): Record<TreatmentStatus, number> {
  return {
    completed: card.completedValue,
    scheduled: card.scheduledValue,
    broken: card.brokenValue,
    open: card.value - card.brokenValue,
  }
}

export function bigMoney(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `$${Math.round(value / 1_000)}k`
  return formatCurrency(value)
}

/** Keeps one decimal in the thousands so 8,123 reads 8.1k rather than 8k. */
export function microMoney(value: number): string {
  if (value >= 10_000) return `$${Math.round(value / 1_000)}k`
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}k`
  return formatCurrency(value)
}

/** What a visit is for: the distinct categories of its codes, in code order. */
export function visitWords(codes: string[], categoryOf: CategoryResolver): string[] {
  return [...new Set(codes.map(categoryOf))]
}

/**
 * The next visit, and whether this plan rides on it.
 *
 * `nextVisitCodes` only ever holds this plan's own booked procedures, so any
 * code at all means the plan is on that visit, sealants and cleanings
 * included. It used to be decided by CDT range instead, which read a plan of
 * six booked sealants as "a cleaning, none of this treatment is on it".
 * The treatment code, when there is one, names the visit.
 *
 * A visit carrying nothing of the plan is named after the appointment, or
 * spelled from its codes' categories when the practice left the name blank.
 */
export function nextVisit(
  card: TreatmentCard,
  categoryOf: CategoryResolver,
): { on: string; word: string; isTreatment: boolean } | undefined {
  if (!card.nextVisitOn) return undefined
  const codes = card.nextVisitCodes ?? []
  if (codes.length > 0) {
    const lead = codes.find((code) => isTreatmentCode(code, categoryOf)) ?? codes[0]
    return { on: card.nextVisitOn, word: categoryOf(lead), isTreatment: true }
  }
  const outside = (card.outsideVisits ?? []).find((visit) => visit.on === card.nextVisitOn)
  const word = outside?.name || visitWords(outside?.codes ?? [], categoryOf).join(`, `) || `Visit`
  return { on: card.nextVisitOn, word, isTreatment: false }
}

/**
 * What the case is made of, by the workspace's category for each code, biggest
 * money first. Unscheduled work only, which is the same set the headline is
 * summed from: grouping the whole case puts a breakdown under the headline that
 * adds to a different number.
 */
export function procedureGroups(
  card: TreatmentCard,
  categoryOf: CategoryResolver,
): { label: string; count: number; fee: number; rest?: boolean }[] {
  const groups = new Map<string, { count: number; fee: number }>()
  const unscheduled = card.procedures.filter(
    (procedure) => !procedure.dead && (procedure.status === `open` || procedure.status === `broken`),
  )
  for (const procedure of unscheduled) {
    const key = categoryOf(procedure.code)
    const current = groups.get(key) ?? { count: 0, fee: 0 }
    groups.set(key, { count: current.count + 1, fee: current.fee + procedure.fee })
  }
  const ranked = [...groups.entries()].sort((a, b) => b[1].fee - a[1].fee)
  const shown = ranked.slice(0, 3).map(([label, group]) => ({ label, count: group.count, fee: group.fee }))
  const tail = ranked.slice(3)
  const restCount = tail.reduce((sum, [, group]) => sum + group.count, 0)
  const restFee = tail.reduce((sum, [, group]) => sum + group.fee, 0)
  return restCount > 0 ? [...shown, { label: `and more`, count: restCount, fee: restFee, rest: true }] : shown
}
